import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { supabase } from '../lib/supabase.ts';
import { answerQueue } from '../lib/answerQueue.ts';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  /** True until the stored session has been restored (or found missing). */
  restoring: boolean;
  /** Set when the session ended unexpectedly (expired / revoked). */
  expired: boolean;
  /** Set after the user signs out on purpose (until the next sign-in). */
  signedOut: boolean;
  signOut(): Promise<void>;
  markExpired(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [expired, setExpired] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userIdRef = useRef<string | null>(null);
  const intentionalSignOut = useRef(false);

  const applySession = useCallback(
    (next: Session | null) => {
      const nextId = next?.user.id ?? null;
      if (nextId !== userIdRef.current) {
        // Account changed: never show one account's cached data to another.
        queryClient.clear();
        answerQueue.setUser(nextId);
        userIdRef.current = nextId;
      }
      setSession(next);
    },
    [queryClient],
  );

  useEffect(() => {
    const client = supabase();
    let active = true;
    client.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        applySession(data.session);
      })
      .finally(() => active && setRestoring(false));

    const { data: sub } = client.auth.onAuthStateChange((event, next) => {
      if (event === 'SIGNED_OUT') {
        if (!intentionalSignOut.current && userIdRef.current) setExpired(true);
        intentionalSignOut.current = false;
      }
      if (event === 'SIGNED_IN') {
        setExpired(false);
        setSignedOut(false);
      }
      applySession(next);
      if (event === 'PASSWORD_RECOVERY') navigate('/reset-password', { replace: true });
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [applySession, navigate]);

  const signOut = useCallback(async () => {
    intentionalSignOut.current = true;
    setSignedOut(true);
    answerQueue.clear();
    await supabase().auth.signOut({ scope: 'local' });
    queryClient.clear();
    setExpired(false);
    navigate('/signin', { replace: true });
  }, [navigate, queryClient]);

  const markExpired = useCallback(() => {
    setExpired(true);
    void supabase().auth.signOut({ scope: 'local' });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, user: session?.user ?? null, restoring, expired, signedOut, signOut, markExpired }),
    [session, restoring, expired, signedOut, signOut, markExpired],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
